import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { supabase } from "@/integrations/supabase/client";
import { deleteAllMyData } from "@/lib/data-deletion.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { clearStoredConsent } from "@/components/GdprConsentModal";
import { toast } from "sonner";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Mon compte & confidentialité — Glow Up Coach" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AccountPage,
});

const CONFIRM_WORD = "SUPPRIMER";

function AccountPage() {
  useDocumentTitleKey("account.head.title");
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const remove = useServerFn(deleteAllMyData);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<{
    deletedAnalyses: number;
    deletedAt: string;
    region: string;
    cancellations: { subscriptionId: string; refundedAmount: number; currency: string | null }[];
  } | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else {
        setAuthed(true);
        setEmail(data.session.user.email ?? null);
      }
    });
  }, [nav]);

  async function onDelete() {
    if (confirm !== CONFIRM_WORD) return;
    setLoading(true);
    try {
      const res = await remove({ data: { environment: getStripeEnvironment() } });
      clearStoredConsent();
      try {
        Object.keys(window.localStorage)
          .filter((k) => k.startsWith("symmetria."))
          .forEach((k) => window.localStorage.removeItem(k));
      } catch {}
      setDone({
        deletedAnalyses: res.deletedAnalyses,
        deletedAt: res.deletedAt,
        region: res.region,
        cancellations: (res.cancellations ?? []).map((c) => ({
          subscriptionId: c.subscriptionId,
          refundedAmount: c.refundedAmount,
          currency: c.currency,
        })),
      });
      setOpen(false);
      toast.success(t("account.toast.deleted"));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t("account.toast.error"));
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    nav({ to: "/" });
  }

  if (!authed) return null;

  const localeTag = locale === "fr" ? "fr-FR" : locale === "nl" ? "nl-NL" : "en-GB";

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "var(--font-sans)" }}>
      <PaymentTestModeBanner />
      <Header />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{t("account.label")}</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 mb-3 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {t("account.title")}
        </h1>
        <p className="text-muted-foreground mb-10">
          {t("account.signed_in_as")} <span className="font-semibold text-foreground">{email}</span>. {t("account.eu_note")} <strong>{t("account.eu")}</strong>.
        </p>

        <div className="rounded-3xl border border-foreground/10 p-6 md:p-8 mb-8">
          <h2 className="font-display font-bold text-xl mb-2" style={{ fontFamily: "var(--font-display)" }}>{t("account.stored.title")}</h2>
          <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
            <li><strong className="text-foreground">{t("account.stored.selfies")} :</strong> {t("account.stored.selfies_text")}</li>
            <li><strong className="text-foreground">{t("account.stored.history")} :</strong> {t("account.stored.history_text")}</li>
            <li><strong className="text-foreground">{t("account.stored.consent")} :</strong> {t("account.stored.consent_text")}</li>
          </ul>
        </div>

        <div className="rounded-3xl border-2 border-destructive/40 bg-destructive/5 p-6 md:p-8">
          <h2 className="font-display font-bold text-xl mb-2 text-destructive" style={{ fontFamily: "var(--font-display)" }}>{t("account.delete.title")}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t("account.delete.desc")}</p>

          {done ? (
            <div className="rounded-2xl bg-background border border-foreground/10 p-5 text-sm">
              <p className="font-bold mb-2">{t("account.delete.done")}</p>
              <ul className="text-muted-foreground space-y-1">
                <li>{t("account.delete.analyses_deleted", done.deletedAnalyses)}</li>
                {done.cancellations.length > 0 ? (
                  done.cancellations.map((c) => (
                    <li key={c.subscriptionId}>
                      {t("account.delete.sub_canceled", c.subscriptionId)}
                      {c.refundedAmount > 0 && c.currency
                        ? ` · ${t("account.delete.refund", (c.refundedAmount / 100).toFixed(2), c.currency.toUpperCase())}`
                        : ` · ${t("account.delete.no_refund")}`}
                    </li>
                  ))
                ) : (
                  <li>{t("account.delete.no_sub")}</li>
                )}
                <li>{t("account.delete.region")} {done.region}</li>
                <li>{t("account.delete.at")} {new Date(done.deletedAt).toLocaleString(localeTag)}</li>
              </ul>
              <button onClick={signOut} className="mt-4 bg-foreground text-background px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90">
                {t("account.delete.sign_out")}
              </button>
            </div>
          ) : !open ? (
            <button onClick={() => setOpen(true)} className="bg-destructive text-destructive-foreground px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90">
              {t("account.delete.request")}
            </button>
          ) : (
            <div className="space-y-4">
              <p className="text-sm">
                {t("account.delete.confirm_prompt")} <code className="px-2 py-0.5 bg-foreground/10 rounded font-mono">{CONFIRM_WORD}</code> {t("account.delete.confirm_suffix")}
              </p>
              <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={CONFIRM_WORD}
                className="w-full px-4 py-3 rounded-xl border border-foreground/20 bg-background font-mono text-sm focus:outline-none focus:ring-2 focus:ring-destructive"
                autoFocus
              />
              <div className="flex gap-3">
                <button onClick={onDelete} disabled={confirm !== CONFIRM_WORD || loading} className="bg-destructive text-destructive-foreground px-5 py-2.5 rounded-full text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
                  {loading ? t("account.delete.deleting") : t("account.delete.confirm_btn")}
                </button>
                <button onClick={() => { setOpen(false); setConfirm(""); }} disabled={loading} className="px-5 py-2.5 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground">
                  {t("account.delete.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 text-center">
          <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground">{t("account.delete.back")}</Link>
        </div>
      </section>
    </div>
  );
}
